import React, { useState } from 'react';
import {
  Box,
  Stack,
  Title,
  TextInput,
  Button,
  Alert,
  Container,
  Card,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { forgotPassword } from '../../services/api';
import { ForgotPasswordInputModel } from '../../types/models';
import { primaryColor } from '../../constants/theme';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmail = (email: string): string | null => {
  if (!email) return null;
  if (!EMAIL_REGEX.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(null);
  };

  const handleResetPasswordClick = async () => {
    // Validate email before submission
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    const forgotPasswordInput: ForgotPasswordInputModel = {
      email,
    };

    try {
      await forgotPassword(forgotPasswordInput);
      setSuccessMessage('An email to reset your password has now been sent.');
      setEmail('');
      setEmailError(null);
    } catch (error) {
      setErrorMessage(
        'There was an error sending the reset password email. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      size="xs"
      px={{ base: 'md', sm: 'lg' }}
      py={{ base: 'md', sm: 'xl' }}
    >
      <Card withBorder shadow="sm" radius="md" bg="white">
        <Stack gap="lg">
          <Box ta="center">
            <img
              src="/logo.png"
              alt="CircleBe Logo"
              style={{ width: 160, marginBottom: 16 }}
            />
          </Box>

          <Title order={2} fw="normal">
            Forgot Password
          </Title>

          <TextInput
            data-testid="forgot-password-email-input"
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => handleEmailChange(e.currentTarget.value)}
            error={emailError}
            required
            disabled={isLoading}
          />

          <Button
            data-testid="forgot-password-submit-button"
            fullWidth
            color={primaryColor}
            onClick={handleResetPasswordClick}
            disabled={isLoading || !email}
            loading={isLoading}
          >
            Reset Password
          </Button>

          {successMessage && (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              title="Success"
              data-testid="forgot-password-success-alert"
            >
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Error"
              data-testid="forgot-password-error-alert"
            >
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </Card>
    </Container>
  );
};
